import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border px-6 py-4">
        <div className="mx-auto flex max-w-4xl items-center gap-3">
          <Link href="/">
            <Button variant="ghost" size="sm" data-testid="button-back">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to HiPo AI Coach
            </Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-12">
        <div className="prose prose-zinc dark:prose-invert max-w-none">
          <h1 className="text-3xl font-semibold" data-testid="heading-privacy-policy">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground">
            <strong>Last Updated:</strong> August 30, 2025
          </p>

          <p>
            We at HiPo Career Solutions LLP (hereinafter referred to as "Company", "we", "us", or "our") value your personal information. This Privacy Policy (hereinafter
            referred to as "Policy") sets out how we collect, use, share, and protect your personal data when you use the website hipoaicoach.com ( referred to as the "Platform").
          </p>

          <p>
            This Policy is issued along with our Terms of Use (hereinafter referred to as "Terms"). Any words and phrases defined under the Terms shall have the same meaning under this Policy unless new meanings are assigned hereunder.
          </p>

          <p>
            By using the Platform and/or our services, or by providing your data to us, you acknowledge that you have read and understood this Policy and consent to the handling of your data as described herein. We are committed to complying with
            applicable data protection laws in India, including the Information Technology Act, 2000 ("IT Act") and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011 and other
            rules made thereunder. We will also ensure compliance with the Digital Personal Data Protection Act, 2023 ("DPDP Act") once it comes into effect, and will update this Policy to align with any new legal requirements as necessary and to the extent applicable.
          </p>

          <section>
            <h2>1. Definitions</h2>
            <p>To help you understand how we handle your data, the following are some of the key terms used in this Policy:</p>
            <ul className="list-disc pl-6">
              <li>
                <strong>"Applicable Laws"</strong> refer to all relevant laws and regulations in force in India that govern how we collect, use, store, or process your Data.
              </li>
              <li>
                <strong>"Data"</strong> means any information related to you that you share with us or that we collect when you use our Platform or services. This includes both information that can directly identify you (like your name) and information that can indirectly identify you (like your device information).
              </li>
              <li>
                <strong>"Personal Data"</strong> refers to any information that relates to an individual and can be used to identify them, either directly (such as a name or ID number) or indirectly (such as online identifiers, location data, or characteristics specific to your physical, mental, or social identity).
              </li>
              <li>
                <strong>"Processing"</strong> means any action that we take with your Data. This includes collection, recording, organisation, structuring, storing, adaptation, altering, retrieval, using, alignment or combination, indexing, disclosure by transmission, dissemination or otherwise making available, restriction, erasure or destruction of Data.
              </li>
              <li>
                <strong>"Sensitive Personal Data"</strong> means the Personal Data that consists of information relating to your password, financial information such as bank account or credit card or debit card or other payment instrument details, health-related information, sexual orientation, related details provided by you for accessing the Platform and/or using the services.
              </li>
            </ul>
          </section>

          <section>
            <h2>2. Data We Collect</h2>
            <p>The Data we collect may include the following:</p>

            <h3>a) Registration and creating an account</h3>
            <p>
              During registration and creation of an account on our Platform, we may collect Data such as your name, email ID, mobile number, name of the organisation you are employed with, current designation, years of experience, the industry/sector in which you work, your age, country and other such Data to personalize your coaching experience.
            </p>
            <p>
              In case you choose to use any third party login authentication service to login to the Platform or create an account, we may collect Data such as your email address, name, and profile picture. After registration, you will have the option to add a profile picture after creating a profile on the Platform.
            </p>
            <p>
              We may also ask for information about your goals or reasons for using the Platform (for example, what you hope to achieve with the HiPo AI Coach) to tailor our services better.
            </p>

            <h3>b) Usage of Platform or Services</h3>
            <p>
              We collect certain Data from you in two primary ways: (i) automatically, when you access or interact with the Platform; and (ii) through any content or Data you choose to provide, input or generate while using the Platform.
            </p>
            <p>
              <strong>User-Generated Content:</strong> This includes any content you input or generate while using the Platform, such as the conversations or questions you pose to the HiPo AI Coach, your self-assessment responses, feedback you provide, journal or diary entries, habit tracking information, inputs to psychometric tools, talent/development reports, and testimonials you choose to share for insight generation.
            </p>
            <p>
              <strong>Activity and Device Information:</strong> We collect information about how you access and use the Platform. This includes technical information like your IP address, browser type, device type, operating system, and timestamps of your visits. We also track your activity on the Platform, such as features you use, pages viewed, and the frequency and duration of your sessions.
            </p>
            <p>
              <strong>Logs and Analytics:</strong> Our systems automatically log certain information to help us diagnose problems and improve our Platform. We may use internal or third-party analytics tools to analyse usage data, but this information is generally in aggregate form and does not identify you personally.
            </p>

            <h3>c) Financial Information</h3>
            <p>
              If you choose to upgrade your current subscription or increase your usage limits on the Platform, your financial information (such as billing name, contact details, payment method details) will be collected and processed by third party payment processors (e.g., PhonePe) integrated with or linked to the Platform. We do not store or have access to your complete payment details.
            </p>

            <h3>d) Cookies</h3>
            <p>
              We also employ “Cookies” for functionality and analytics, as detailed in this Policy. We use this data to enhance your experience (for example, keeping you logged in or remembering your preferences) and to gather analytics about our user base.
            </p>
          </section>

          <section>
            <h2>3. Purpose of Processing Data</h2>

            <h3>a) Provision and maintenance of Services</h3>
            <p>
              We process your Data to provide access to the Platform, create and manage your account, and render our Services to you. This includes enabling personalized coaching interactions through the HiPo AI Coach, supporting habit tracking and journaling features, and generating personal insights and analytics.
            </p>

            <h3>b) Service improvement and development</h3>
            <p>
              We may analyze your interactions with the Platform, including coaching conversations and behavioural patterns, to enhance user experience, improve existing features, and develop new ones. We may also use anonymized and aggregated data derived from your usage to refine our AI models and improve our Services overall.
            </p>

            <h3>c) Assess and protect the Platform</h3>
            <p>
              We may use the Data shared to prevent, detect and identify any cybersecurity incidents and improve our cybersecurity mechanisms. We may also use the Data to conduct internal reviews or audits of the functioning of the Platform and the Services.
            </p>

            <h3>d) Communication</h3>
            <p>
              We may use your Data to communicate with you and send service-related communications and alerts such as habit reminders, progress reports, account updates, and responses to your inquiries. We may also send notifications and announcements about the Platform and/or our Services.
            </p>
          </section>

          <section>
            <h2>4. Sharing of Data</h2>
            <p>
              We may share your Data with trusted third parties such as payment processors, analytics providers, cloud service providers, and legal or regulatory authorities as required. Any sharing is done in compliance with this Policy and applicable laws.
            </p>
          </section>

          <section>
            <h2>5. Data Storage and Security</h2>
            <p>
              We implement reasonable security measures to safeguard your Data. While we strive to protect your personal data, no method of transmission over the Internet or electronic storage is completely secure. We continuously review and update our security practices to mitigate risks.
            </p>
          </section>

          <section>
            <h2>6. Data Retention</h2>
            <p>
              We retain your Data for as long as necessary to fulfil the purposes outlined in this Policy or as required by Applicable Laws. When Data is no longer required, we will delete or anonymize it in accordance with our data retention practices.
            </p>
          </section>

          <section>
            <h2>7. Your Rights and Choices</h2>
            <p>
              Subject to Applicable Laws, you may have the right to access, correct, update, or delete your Personal Data. You may also have the right to withdraw consent, restrict processing, or request data portability. To exercise your rights, please contact us at the email address provided below.
            </p>
          </section>

          <section>
            <h2>8. Children’s Privacy</h2>
            <p>
              Our Platform and Services are not intended for individuals under 18 years of age. We do not knowingly collect Personal Data from minors. If you believe that a minor has provided us with Personal Data, please contact us so we can take appropriate steps to delete such data.
            </p>
          </section>

          <section>
            <h2>9. Cross-Border Data Transfers</h2>
            <p>
              Your Data may be transferred to and stored on servers located outside India. By using the Platform or providing us your Data, you consent to such transfer, provided that adequate safeguards are in place to protect your Data.
            </p>
          </section>

          <section>
            <h2>10. Changes to this Policy</h2>
            <p>
              We may update this Policy from time to time to reflect changes in legal or regulatory requirements or in our data processing practices. We will notify you of material changes by updating the “Last Updated” date or through other appropriate means. Your continued use of the Platform after any changes signifies your acceptance of the updated Policy.
            </p>
          </section>

          <section>
            <h2>11. Contact Information</h2>
            <p>If you have any questions or concerns regarding this Policy or our data practices, please contact our Grievance Officer at:</p>
            <p>
              <strong>Name:</strong> Kanti Kovvali
              <br />
              <strong>Email:</strong> contactus@hipo.co.in
              <br />
              <strong>Contact No.:</strong> 095-97519191
              <br />
              <strong>Address:</strong> Plot No. D, 5th Rd, Shree Krishna Nagar, Marol MIDC Industry Estate, Andheri East, Mumbai, Maharashtra 400093
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
